import { TFunction, Trans, useTranslation } from 'react-i18next';
import { useHistory, useParams, useRouteMatch } from 'react-router-dom';
import {
	Button,
	Dropdown,
	Empty,
	Menu,
	Modal,
	Spin,
	Table,
	TablePaginationConfig,
} from 'antd';
import styles from './LeaderboardsPage.module.less';
import { useCallback, useEffect, useMemo, useState } from 'react';
import api, {
	LeaderboardResetFrequency,
	LeaderboardSortField,
	LeaderboardSortOrder,
	ListLeaderboardsDto,
	ListLeaderboardsDtoPagedResult,
} from '../../../api';
import {
	getAPISorter,
	getCellValue,
	getShowTotal,
} from '../../../utils/tableUtils';
import { EllipsisOutlined, InfoCircleOutlined } from '@ant-design/icons';
import {
	TLeaderboardResetFrequency,
	TLeaderboardSortOrder,
} from '../../../utils/productUtils';
import { SorterResult, SortOrder } from 'antd/lib/table/interface';
import Breadcrumb, { IRoutes } from '../../../components/Breadcrumb/Breadcrumb';
import { useSelector } from 'react-redux';
import { projectSelector } from '../../../redux/selectors/projectSelector';
import { History } from 'history';
import OmniNotification from 'components/OmniNotification';

type ParamsProps = {
	projectId: string;
};

type HandleDeleteParams = {
	productId: string;
	apiName: string;
};

type SorterType =
	| SorterResult<ListLeaderboardsDto>
	| SorterResult<ListLeaderboardsDto>[];

const DefaultSorter = {
	order: 'descend' as SortOrder,
	column: {
		APISortField: LeaderboardSortField.Title,
	} as any,
};

const getSortOrder = (
	sorterRef: SorterResult<ListLeaderboardsDto>,
	field: LeaderboardSortField,
) => {
	return (sorterRef?.column as any)?.APISortField === field
		? sorterRef.order
		: undefined;
};

export default function LeaderboardsPage() {
	const { t } = useTranslation();
	const history = useHistory();
	const route = useRouteMatch();
	const [leaderboards, setLeaderboards] =
		useState<ListLeaderboardsDtoPagedResult>();
	const [isLoading, setIsLoading] = useState(false);
	const { projectId } = useParams<ParamsProps>();
	const [sorterRef, setSorterRef] = useState<SorterType>(DefaultSorter);
	const [routes, setRoutes] = useState<IRoutes[]>([]);
	const projectInfo = useSelector(projectSelector);
	const showTotal = useCallback(
		(total: number, range: [number, number]) => getShowTotal(total, range, t),
		[t],
	);
	const [pagination, setPagination] = useState<TablePaginationConfig>({
		current: 1,
		pageSize: 10,
		size: 'default',
		showQuickJumper: true,
		showTotal,
		showSizeChanger: true,
	});

	function navigateToCreateLeaderboards() {
		history.push(`${route.url}/create`);
	}

	useEffect(() => {
		handleTableChange(pagination, null, DefaultSorter);
	}, []);

	useEffect(() => {
		setRoutes([
			{
				path: `project/${projectInfo?.id}`,
				breadcrumbName: projectInfo?.name as string,
			},
			{
				path: `game-services`,
				breadcrumbName: t('gameServicesPage.header'),
			},
			{
				path: '',
				breadcrumbName: t('leaderboardsPage.header'),
			},
		]);
	}, [projectInfo?.id, projectInfo?.name]);

	const getActionMenu = (
		t: TFunction,
		record: ListLeaderboardsDto,
		history: History,
		url: string,
	) => {
		return (
			<Menu>
				<Menu.Item
					key="edit"
					data-testid="editLeaderboardButton"
					onClick={() => {
						history.push(`${url}/${record.apiName}/edit`);
					}}
				>
					{t('leaderboardsPage.edit')}
				</Menu.Item>
				<Menu.Item
					key="delete"
					danger={true}
					onClick={() => {
						openDeleteModal(projectInfo?.productId!, record.apiName!);
					}}
				>
					{t('leaderboardsPage.delete')}
				</Menu.Item>
			</Menu>
		);
	};

	const getColumns = (
		t: TFunction,
		sorterRef: SorterResult<ListLeaderboardsDto>,
		history: History,
		url: string,
	) => {
		return [
			{
				title: t('leaderboardsPage.apiName'),
				render: (record: ListLeaderboardsDto) => getCellValue(record.apiName),
				sorter: true,
				ellipsis: true,
				sortOrder: getSortOrder(sorterRef, LeaderboardSortField.ApiName),
				APISortField: LeaderboardSortField.ApiName,
			},
			{
				title: t('leaderboardsPage.title'),
				render: (record: ListLeaderboardsDto) => getCellValue(record.title),
				sorter: true,
				ellipsis: true,
				sortOrder: getSortOrder(sorterRef, LeaderboardSortField.Title),
				APISortField: LeaderboardSortField.Title,
				width: 250,
			},
			{
				title: t('leaderboardsPage.gameStat'),
				render: (record: ListLeaderboardsDto) =>
					getCellValue(record.gameStatApiName),
				sorter: true,
				ellipsis: true,
				sortOrder: getSortOrder(sorterRef, LeaderboardSortField.GameStat),
				APISortField: LeaderboardSortField.GameStat,
			},
			{
				title: t('leaderboardsPage.sortOrder'),
				render: (record: ListLeaderboardsDto) =>
					getCellValue(
						t(TLeaderboardSortOrder[record?.sortOrder as LeaderboardSortOrder]),
					),
				sorter: true,
				sortOrder: getSortOrder(sorterRef, LeaderboardSortField.SortOrder),
				APISortField: LeaderboardSortField.SortOrder,
				width: 150,
			},
			{
				title: t('leaderboardsPage.resetFrequency'),
				render: (record: ListLeaderboardsDto) =>
					getCellValue(
						t(
							TLeaderboardResetFrequency[
								record?.resetFrequency as LeaderboardResetFrequency
							],
						),
					),
				sorter: true,
				sortOrder: getSortOrder(sorterRef, LeaderboardSortField.ResetFrequency),
				APISortField: LeaderboardSortField.ResetFrequency,
				width: 150,
			},
			{
				title: t('leaderboardsPage.actions'),
				render: (record: ListLeaderboardsDto) => (
					<Dropdown
						overlay={getActionMenu(t, record, history, url)}
						trigger={['click']}
					>
						<EllipsisOutlined
							data-testid="leaderboardActions"
							onClick={(e) => e.preventDefault()}
						/>
					</Dropdown>
				),
				width: 150,
			},
		];
	};

	const columns = useMemo(
		() =>
			getColumns(
				t,
				sorterRef as SorterResult<ListLeaderboardsDto>,
				history,
				route.url,
			),
		[t, sorterRef, history, getColumns],
	);

	const openDeleteModal = (productId: string, apiName: string) => {
		const deleteParams: HandleDeleteParams = {
			productId,
			apiName,
		};
		Modal.confirm({
			icon: <InfoCircleOutlined style={{ color: 'red' }} />,
			title: (
				<span className={styles.warningModalHeader}>
					{t('leaderboardsPage.deleteModal.header')}
				</span>
			),
			okText: t('leaderboardsPage.deleteModal.okButton'),
			cancelText: t('common.cancel'),
			onOk() {
				return handleDeleteLeaderboard(deleteParams);
			},
			cancelButtonProps: {
				className: 'color-dark',
			},
			content: (
				<span>
					{t('leaderboardsPage.deleteModal.body', {
						apiName,
					})}
				</span>
			),
		});
	};

	const handleDeleteLeaderboard = async (data: HandleDeleteParams) => {
		if (!data.productId) return;
		try {
			await api.deleteLeaderboardDefinition(data.productId, data.apiName);
			OmniNotification.success({
				message: t('leaderboardsPage.deleteMessage'),
				description: t('leaderboardsPage.deleteDescription', {
					apiName: data.apiName,
				}),
			});
			await handleTableChange(pagination, null, sorterRef);
		} catch (e) {
			OmniNotification.error({
				message: t('notification.error.title'),
				description: t('api.defaultError'),
			});
		} finally {
		}
	};

	const handleTableChange = async (
		tablePagination: TablePaginationConfig,
		filters: any,
		sorter: SorterType,
	) => {
		setSorterRef(sorter);

		const { sortOrder, sortField } = getAPISorter(sorter);

		setIsLoading(true);

		api
			.getLeaderboardsList(
				Number.parseInt(projectId),
				sortOrder,
				sortField,
				(tablePagination.current as number) - 1,
				tablePagination.pageSize as number,
			)
			.then((leaderboards) => {
				setIsLoading(false);
				setLeaderboards(leaderboards);
				setPagination({
					...pagination,
					...tablePagination,
					total: leaderboards.totalCount || 0,
				});
			})
			.catch(() => {
				setIsLoading(false);
			});
	};

	return (
		<>
			<div className={styles.pageContainer}>
				<Breadcrumb routes={routes} />
				<div className="page-head">
					<div>
						<h2 className="h2" data-testid="page-title">
							{t('leaderboardsPage.header')}
						</h2>
					</div>
					<div className={styles.buttonContainer}>
						<Button
							type="primary"
							data-testid="create-leaderboard-button"
							onClick={() => navigateToCreateLeaderboards()}
						>
							{t('leaderboardsPage.create')}
						</Button>
					</div>
				</div>
				{!leaderboards && <Spin />}
				{leaderboards && leaderboards.items?.length === 0 && (
					<div
						className={styles.emptyContainer}
						data-testid={'emptyDataDescription'}
					>
						<p>
							<span>{t('leaderboardsPage.noLeaderboards.line1')}</span>
							<br />
							<span>{t('leaderboardsPage.noLeaderboards.line2')}</span>
						</p>
					</div>
				)}
				{leaderboards && (leaderboards.items as any)?.length > 0 && (
					<div>
						<Table
							className="table"
							size="middle"
							rowKey="id"
							loading={isLoading}
							rowClassName={styles.tableRow}
							columns={columns}
							dataSource={leaderboards.items}
							onChange={handleTableChange}
							sortDirections={['descend', 'ascend', 'descend']}
							data-testid="leaderboardsTable"
							pagination={pagination}
						/>
					</div>
				)}
			</div>
		</>
	);
}
